import React, { FC, useCallback, useState } from 'react';
import { Heading } from '@components/Atoms/Heading';
import StyledStuckInAContractForm from './styled/StyledStuckInAContractForm';
import StyledContainer from './styled/StyledContainer';
import { CheckoutFormRow } from '@components/Organisms/OrderForms/CheckoutFormRow';
import { Button, CheckBox, Input, Spinner } from '@components/Atoms';
import { ButtonType } from '@components/Atoms/Button/types';
import StyledButtonRow from '@components/Organisms/OrderForms/styled/StyledButtonRow';
import StyledForm from '@factories/RowFactory/components/StuckInAContractForm/styled/StyledForm';
import { renderParagraphs } from '@lib/utils';
import StyledCheckoutCheckbox from '@components/Organisms/OrderForms/styled/StyledCheckoutCheckbox';
import { productController } from '@controllers/index';
import { InputType } from '@components/Atoms/Input/types';
import StyledCheckoutFormRow from '@components/Organisms/OrderForms/styled/StyledCheckoutFormRow';
import StyledPostcodeScroll from '@components/Organisms/Postcode/styled/StyledPostcodeScroll';
import StyledPostcodeDropdown from '@components/Organisms/Postcode/styled/StyledPostcodeDropdown';
import { UprnItemT, UprnStatus } from '@lib/types';

export const StuckInAContractForm: FC<{
  Title: string;
  howWeUseYourDataText: string;
  display: boolean;
}> = ({ Title, howWeUseYourDataText, display }) => {
  const { customerCallMeServer } = productController;
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const rows = [
    { key: 'contact_name', label: 'First Name', required: true, value: '' },
    { key: 'contact_surname', label: 'Last Name', required: true, value: '' },
    { key: 'email', label: 'Email', required: true, value: '' },
    { key: 'phone', label: 'Phone Number', required: true, value: '' },
    { key: 'current_provider', label: 'Current Provider', required: true, value: '' },
    {
      key: 'ending_current',
      label: 'Contract end dates',
      required: true,
      value: '',
      placeholder: 'Date of birth',
      type: 'date',
      autocomplete: 'bday',
    },
    {
      key: 'accept_terms',
      type: 'checkbox',
      required: true,
      value: '',
    },
    {
      key: 'postcode',
      type: 'postcode',
      required: true,
      value: '',
    },
    {
      key: 'uprn',
      type: 'postcode',
      required: true,
      value: '',
    },
  ];

  const defaultFormData = rows.reduce((acc, row) => {
    return {
      ...acc,
      [row.key]: row.value,
    };
  }, {});

  const [formData, setFormData] = useState<any>(defaultFormData);

  const formDataValues: Array<{
    [key: string]: string;
  }> = Object.values(formData);

  const contractEndDate = new Date(formData.ending_current);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const hasExpiredContract = contractEndDate < today;

  const validators = [
    {
      keys: [],
      isValid: formDataValues.every((field) => field),
      message: 'Field is required',
    },
    {
      keys: ['email'],
      isValid: formData.email?.match(/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/),
      message: 'Email is invalid',
    },
    {
      keys: ['phone'],
      isValid: formData.phone?.match(
        /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?#(\d{4}|\d{3}))?$/,
      ),
      message: 'Phone is invalid',
    },
    {
      keys: ['ending_current'],
      isValid: formData.ending_current?.match(/^\d{4}-\d{2}-\d{2}$/),
      message: 'Please enter a valid date. ',
    },
    {
      keys: ['ending_current'],
      isValid: !hasExpiredContract,
      message:
        'Your contract seems to have ended already. That means you can sign up with us right away!',
    },
  ];

  const isValid = validators.every((validator) => validator.isValid);

  const submitForm = useCallback(async () => {
    setHasSubmitted(true);

    try {
      await customerCallMeServer({ args: formData }).then((responseCallMe) => {
        console.log({ responseCallMe });
      });
    } catch (error) {
      console.log(error);
      setHasSubmitted(false);
    }
  }, [customerCallMeServer, formData]);

  const updateFormData = (key: string, value: any) => {
    setFormData((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const [postcodeError, setPostcodeError] = useState<string | undefined>(undefined);
  const [uprns, setUprns] = useState<UprnItemT[] | null>(null);
  const [uprn, setUprn] = useState<UprnItemT | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validatePostcode = async ({ target: { value: postcode } }) => {
    if (formData.postcode !== postcode) {
      setIsLoading(true);
      setPostcodeError(undefined);
      setUprn(null);

      try {
        const postcodeCheck = await productController.checkPostcode({ postcode });
        const uprnList = await productController.getUprnListServer({ postcode });

        updateFormData('postcode', postcode);

        if (uprnList?.length) {
          setUprns(uprnList);
        } else if (postcodeCheck) {
          setUprns([
            {
              uprn: '',
              address: `${postcodeCheck.admin_ward}, ${postcodeCheck.admin_district}, ${postcodeCheck.postcode}`,
              coverage: UprnStatus.NOTPASSED,
              address_type: '',
              postcode: '',
              ismdu: false,
            },
          ]);
        }
      } catch (e) {
        updateFormData('postcode', '');
        updateFormData('uprn', '');
        setUprns(null);
        setPostcodeError('Postcode is invalid');
      }
    }
    setIsLoading(false);
  };

  const selectUprn = (uprn: UprnItemT) => {
    setUprn(uprn);
    updateFormData('uprn', uprn?.uprn);
  };

  if (!display) {
    return null;
  }

  return (
    <StyledStuckInAContractForm id="form">
      <StyledContainer>
        <header className="mb-10">
          <Heading level={2}>{Title}</Heading>
        </header>
        {hasSubmitted ? (
          <p className="submittedText">
            Thank you! Your request has been received. Our team will contact you shortly with your
            no-obligation contract buyout quote.
          </p>
        ) : (
          <form className="flex flex-col items-center" onSubmit={submitForm}>
            <StyledForm>
              {rows
                .filter(({ type }) => type !== 'checkbox' && type !== 'postcode')
                .map(({ key, label, type, required, placeholder }) => (
                  <CheckoutFormRow
                    className="w-1/2"
                    key={key}
                    name={key}
                    label={label}
                    formData={formData}
                    validators={validators}
                    onChange={updateFormData}
                    type={type}
                    required={required}
                    placeholder={placeholder}
                  />
                ))}

              <StyledCheckoutFormRow>
                <Input
                  fullWidth
                  type={InputType.TEXT}
                  key="postcode"
                  name="postcode"
                  label="Postcode"
                  formData={formData}
                  required={true}
                  placeholder="Postcode"
                  onBlur={validatePostcode}
                  error={postcodeError}
                />
              </StyledCheckoutFormRow>
              {uprns?.length && !uprn && (
                <StyledPostcodeDropdown>
                  <StyledPostcodeScroll>
                    <li>
                      <span className="font-bold text-black text-[.8rem] font-geomanist mb-2">
                        Select your address
                      </span>
                    </li>
                    {uprns?.map((row) => (
                      <li key={row?.uprn}>
                        <button onClick={() => selectUprn(row)}>{row?.address}</button>
                      </li>
                    ))}
                  </StyledPostcodeScroll>
                </StyledPostcodeDropdown>
              )}
              {isLoading && (
                <StyledPostcodeDropdown>
                  <Spinner />
                </StyledPostcodeDropdown>
              )}
              {uprn && (
                <div>
                  <StyledCheckoutFormRow>
                    <Input
                      fullWidth
                      type={InputType.TEXT}
                      key="uprn"
                      name="uprn"
                      label="Address"
                      disabled={true}
                      value={uprn.address}
                    />
                  </StyledCheckoutFormRow>
                  <button
                    className="font-bold text-blue-700 text-[.8rem] font-geomanist"
                    onClick={() => setUprn(null)}
                  >
                    change address
                  </button>
                </div>
              )}
            </StyledForm>
            {howWeUseYourDataText ? (
              <p className="mt-8 mb-4 max-w-[800px] text-left">{howWeUseYourDataText}</p>
            ) : null}
            <StyledCheckoutCheckbox>
              <CheckBox
                value="Checkbox"
                label={renderParagraphs({
                  body: '<span>I agree to the [Terms & Conditions](/legal)</span>',
                  hasMarkdown: true,
                })}
                name="accept_terms"
                id="accept_terms"
                checked={formData.accept_terms}
                onClick={(value) => updateFormData('accept_terms', value)}
              />
            </StyledCheckoutCheckbox>
            <StyledButtonRow className="mt-10">
              <Button buttonType={ButtonType.PRIMARY} type="submit" disabled={!isValid}>
                Submit
              </Button>
            </StyledButtonRow>
          </form>
        )}
      </StyledContainer>
    </StyledStuckInAContractForm>
  );
};
